import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import qs from 'qs';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Tooltip,
  DialogContentText,
  MenuItem,
  Popper,
  Paper,
  MenuList,
  ClickAwayListener,
  Typography,
  Icon,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import LiquorIcon from '@mui/icons-material/Liquor';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import CustomTable from 'components/common/customTableV2';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import Modal from 'components/common/modal';
import WinesService from 'services/winesService';
import SuperAdminActionsService from 'services/superAdminActionsService';
import wineStore from 'store/wineStore';
import { adaptWinesTableData } from 'helpers/adapter';
import { replacePath, getMessage } from 'helpers/helper';
import domainData from 'store/domainStore';
import { useModal } from 'hooks/common';
import SidebarStore from 'store/ui/sidebarStore';
import M from 'messages';
import wineListImportService from 'services/wineListImportService';
import { routes } from 'config/index';

import EmptyData from './emptyPage';
import tableOptions from './config/config';
import CustomTableToolbar from './toolbar';
import CopyActionModal from './modals/copyActionModal';
import ImportModal from './modals/importModal';
import WineListImportModal from '../welcome/modals/wineListImportModal';

import classes from './styles';

const Wines = observer(({ path }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserSuperAdmin = domainData.userIsSuperAdmin();
  const currentUser = domainData.getCurrentUser();

  const { enqueueSnackbar } = useSnackbar();
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [dataLoading, setDataLoading] = useState({ loading: false, loaded: false });
  const [fromFilter, setFromFilter] = useState(true);
  const [winesData, setWinesData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [isOpenMove, openModalMove, closeModalMove] = useModal(false);
  const [isCopyAction, openModalCopy, closeModalCopy] = useModal(false);
  const [isImportOpen, openImportModal, closeImportModal] = useModal(false);
  const [isWineListImportOpen, openWineListImportModal, closeWineListImportModal] = useModal(false);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersList, setPartnersList] = useState([]);
  const [importErrors, setImportErrors] = useState(null);
  const isSidebarOpened = SidebarStore.sidebarState;
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);

  const { searchFields, filterFields } = tableOptions.wines;

  // NOTE: cut / symbol
  const pageItem = path.substring(1);

  const setFilter = useCallback(
    filter => {
      replacePath(navigate, filter);
      wineStore.setFilter(filter);
    },
    [navigate],
  );

  const queryParams = wineStore.getCurrentFilterParams(pageParams);

  const getCurrentServiceProvider = useCallback(() => {
    const currentSP = domainData.getCurrentSP();
    return currentSP ? { currentServiceProvider: currentSP } : {};
  }, []);

  const loadPartners = useCallback(async () => {
    if (!isUserSuperAdmin) return;
    try {
      setPartnersLoading(true);
      const response = await domainData.getSPListNames();
      if (response) {
        setPartnersList(response);
      }
    } catch (err) {
      enqueueSnackbar('Error fetching partners list', { variant: 'error' });
    } finally {
      setPartnersLoading(false);
    }
  }, [enqueueSnackbar, isUserSuperAdmin]);

  const getWinesLists = useCallback(
    async (params, withFilter = true) => {
      try {
        setDataLoading(prevState => ({ ...prevState, loaded: false }));
        setFromFilter(withFilter);
        const urlParams = params.params;
        setFromFilter(
          !!((urlParams?.filter && Object.keys(urlParams.filter).length) || urlParams?.search?.value?.length),
        );
        setFilter(params);
        const newParams = {
          params: { ...params.params, ...getCurrentServiceProvider(), includeLists: true, includeDrafts: true },
        };
        const response = await WinesService.getWines(newParams);
        if (response) {
          wineStore.setStoreWines(response.data);
          setWinesData({
            count: response.data.count,
            data: adaptWinesTableData(response.data.data),
            createAbility: response.data.createAbility,
          });
        }
      } catch (err) {
        // TODO: handle error case
        enqueueSnackbar('Get wines lists error', { variant: 'error' });
      } finally {
        setDataLoading({ loading: false, loaded: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, getCurrentServiceProvider],
  );

  useEffect(() => {
    const pp = qs.parse(location.search, { ignoreQueryPrefix: true });
    const qp = wineStore.getCurrentFilterParams(pp);
    getWinesLists(qp);
    wineStore.setPageName(pageItem);
    setFilter(qp);
    setSelectedRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (isImportOpen && !partnersList.length) {
      loadPartners();
    }
  }, [isImportOpen, loadPartners, partnersList.length]);

  const handleCreateMenus = () => {
    navigate(`${location.pathname}/create`);
  };

  const handleWineListImportSubmit = async payload => {
    await wineListImportService.requestWineListImport(payload);
  };

  const handleOpenCreateMenu = event => {
    setCreateMenuAnchorEl(event.currentTarget);
  };

  const handleCloseCreateMenu = () => {
    setCreateMenuAnchorEl(null);
  };

  const handleCreateManually = () => {
    handleCloseCreateMenu();
    handleCreateMenus();
  };

  const handleImportWineList = () => {
    handleCloseCreateMenu();
    openWineListImportModal();
  };

  const handleFindWineFromLibrary = () => {
    handleCloseCreateMenu();
    navigate(routes.wineLibraryBrowse.path);
  };

  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getWinesLists(newFilter);
    }
  };

  const onFilterCallback = (filterObj, reset = false) => {
    if (reset) {
      wineStore.resetFilters();
      const newParams = wineStore.getCurrentFilterParams();
      setDataLoading({ loading: true, loaded: false });
      getWinesLists(newParams, false);
      return;
    }
    if (filterObj && !Array.isArray(filterObj)) {
      const fieldKeyList = Object.keys(filterObj);
      let newFilter = {};
      fieldKeyList.forEach(i => {
        const fieldValue = filterObj[i];
        let oldValue = queryParams.params?.filter?.[i];
        if (oldValue === undefined) {
          oldValue = '';
        }
        if (filterFields && oldValue !== fieldValue && fieldValue !== undefined) {
          newFilter = {
            ...newFilter,
            params: {
              ...queryParams.params,
              ...newFilter.params,
              filter: { ...queryParams.params.filter, ...newFilter?.params?.filter, [i]: fieldValue },
              skip: 0,
            },
          };
        }
      });
      if (Object.keys(newFilter).length) {
        setFilter(newFilter);
        getWinesLists(newFilter);
      }
    }
  };

  const handleDeleteWine = async () => {
    try {
      const response = await WinesService.deleteWineMany({ ids: selectedRows });
      if (response) {
        enqueueSnackbar(getMessage(response.data), { variant: 'success' });
      }
      await getWinesLists(queryParams, false);
      setSelectedRows([]);
    } catch (err) {
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
    }
    closeModalMove();
  };

  const handleCopyAction = async (rows, serviceProviderId, action) => {
    try {
      setMoveLoading(true);
      await SuperAdminActionsService.copyToWines({
        serviceProviderId,
        ids: rows,
        action,
      });
      enqueueSnackbar('Wines copied successfully', { variant: 'success' });
      await getWinesLists(queryParams, false);
      setMoveLoading(false);
      setSelectedRows([]);
      closeModalCopy();
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    }
  };

  const handleCloneAction = async row => {
    handleCopyAction([row.id], row.serviceProviderId, 'clone');
  };

  const handleClickDeleteIcon = () => {
    setMoveLoading(false);
    openModalMove();
  };

  const handleClickCopyIcon = async () => {
    openModalCopy();
  };

  const handleCloseImportModal = () => {
    setImportErrors(null);
    closeImportModal();
  };

  const handleImport = async (importFile, importPartnerId) => {
    if (!importFile) {
      enqueueSnackbar('Please select a file to import', { variant: 'error' });
      return { success: false };
    }
    if (isUserSuperAdmin && !importPartnerId) {
      enqueueSnackbar('Please select a partner for import', { variant: 'error' });
      return { success: false };
    }
    try {
      setImportErrors(null);
      const response = await WinesService.importWines(importFile, importPartnerId);
      if (response) {
        enqueueSnackbar(getMessage(response.data), { variant: 'success' });
      }
      await getWinesLists(queryParams, false);
      return { success: true };
    } catch (err) {
      const payload = err?.response?.data;
      if (payload?.errors) {
        setImportErrors(payload.errors);
        return { success: false, validation: true };
      }
      enqueueSnackbar(getMessage(payload, 'error'), { variant: 'error' });
      return { success: false };
    }
  };

  const toolbarView = (
    <CustomTableToolbar
      onSearchCallback={onSearchCallback}
      filteredParams={queryParams}
      numSelected={selectedRows.length}
      filterFields={filterFields}
      onFilterCallback={onFilterCallback}
      handleDelete={handleClickDeleteIcon}
      handleCopy={handleClickCopyIcon}
      isUserSuperAdmin={isUserSuperAdmin}
    />
  );

  const openPath = row => `${location.pathname}/${row.id}`;

  const isEmptyState = () => !fromFilter && !winesData?.data?.length;

  const createButton = disabled => (
    <Button
      onClick={handleCreateMenus}
      startIcon={<AddIcon sx={classes.primaryIcon} />}
      sx={classes.primaryIconButton}
      variant="contained"
      color="primary"
      disabled={disabled}>
      {M.get('actions.create')}
    </Button>
  );

  const createMenuButton = disabled => (
    <Button
      onClick={handleOpenCreateMenu}
      startIcon={<AddIcon sx={classes.primaryIcon} />}
      endIcon={
        <KeyboardArrowDownIcon
          sx={{
            ...classes.primaryIcon,
            transform: createMenuAnchorEl ? 'rotate(180deg)' : 'none',
            transition: theme =>
              theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
          }}
        />
      }
      sx={classes.primaryIconButton}
      variant="contained"
      color="primary"
      disabled={disabled}
      aria-haspopup="true"
      aria-controls={createMenuAnchorEl ? 'wine-create-menu' : undefined}
      aria-expanded={createMenuAnchorEl ? 'true' : undefined}>
      {M.get('actions.addCreateWine')}
    </Button>
  );

  const importButton = () => (
    <Button
      onClick={openImportModal}
      startIcon={<UploadFileIcon sx={classes.primaryIcon} />}
      sx={classes.primaryIconButton}
      variant="outlined"
      color="primary">
      {M.get('actions.import')}
    </Button>
  );

  if (dataLoading.loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        ...classes.root,
        ...(isSidebarOpened && classes.rootDrawerOpen),
        ...(!isSidebarOpened && classes.rootDrawerClose),
      }}>
      <Modal
        open={isOpenMove}
        leftBtnText={M.get('actions.cancel')}
        rightBtnText={M.get('actions.delete')}
        handleClose={closeModalMove}
        handleSubmit={handleDeleteWine}
        title={M.get('wines.deleteConfirmationTitle')}
        submitLoading={moveLoading}
        disableSave={moveLoading}
        isDeleteModal={true}>
        <DialogContentText className={classes.moveToModalView}>
          {M.get('wines.deleteConfirmationText')}
        </DialogContentText>
      </Modal>
      {isUserSuperAdmin && (
        <ImportModal
          isOpen={isImportOpen}
          handleClose={handleCloseImportModal}
          partnersList={partnersList}
          partnersLoading={partnersLoading}
          handleImport={handleImport}
          importErrors={importErrors}
        />
      )}
      {isUserSuperAdmin && (
        <CopyActionModal
          isOpen={isCopyAction}
          handleClose={closeModalCopy}
          selectedRows={selectedRows}
          handleCopyAction={handleCopyAction}
        />
      )}
      {!isUserSuperAdmin && (
        <WineListImportModal
          open={isWineListImportOpen}
          handleClose={closeWineListImportModal}
          handleSubmit={handleWineListImportSubmit}
          serviceProviderId={currentUser?.serviceProviderId}
        />
      )}
      <PageHeader
        generalPageTitle={M.get('wines.pageTitle')}
        element={
          <Grid size={{ xs: 12 }} sx={classes.actionPart}>
            {!(isEmptyState() && dataLoading.loaded) &&
              (!isUserSuperAdmin && !winesData.createAbility ? (
                <Tooltip title={M.get('wines.createLimit')} placement="top">
                  <span>{createMenuButton(true)}</span>
                </Tooltip>
              ) : (
                <>
                  {isUserSuperAdmin ? (
                    <>
                      {importButton()}
                      {createButton()}
                    </>
                  ) : (
                    createMenuButton()
                  )}
                </>
              ))}
          </Grid>
        }
      />
      <Popper
        id="wine-create-menu"
        anchorEl={createMenuAnchorEl}
        open={Boolean(createMenuAnchorEl)}
        placement="bottom-end"
        disablePortal
        modifiers={[
          { name: 'flip', enabled: false },
          { name: 'preventOverflow', enabled: false },
        ]}
        style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleCloseCreateMenu}>
          <Paper elevation={4} sx={classes.createMenuPaper}>
            <MenuList>
              <MenuItem onClick={handleFindWineFromLibrary} sx={{ minHeight: 36 }}>
                <Icon baseClassName="material-symbols-outlined" sx={classes.createMenuListItemIcon}>
                  wine_bar
                </Icon>
                <Typography sx={classes.createMenuListItemText}>{M.get('actions.findWineFromLibrary')}</Typography>
              </MenuItem>
              <MenuItem onClick={handleImportWineList} sx={{ minHeight: 36 }}>
                <Icon baseClassName="material-symbols-outlined" sx={classes.createMenuListItemIcon}>
                  upload_file
                </Icon>
                <Typography sx={classes.createMenuListItemText}>{M.get('actions.importWineList')}</Typography>
              </MenuItem>
              <MenuItem onClick={handleCreateManually} sx={{ minHeight: 36 }}>
                <Icon baseClassName="material-symbols-outlined" sx={classes.createMenuListItemIcon}>
                  edit
                </Icon>
                <Typography sx={classes.createMenuListItemText}>{M.get('actions.createNewWine')}</Typography>
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
      <Grid size={{ xs: 12 }}>
        <Box sx={classes.tableRoot}>
          {isEmptyState() && dataLoading.loaded ? (
            <EmptyData
              icon={<LiquorIcon sx={classes.emptyIcon} />}
              title={pageItem}
              titleClassName="emptyPageTitle"
              onFindFromLibrary={handleFindWineFromLibrary}
              onImportWineList={handleImportWineList}
              onCreateWine={handleCreateManually}
            />
          ) : (
            <Grid container sx={classes.container}>
              <Box sx={classes.tabsContainer}>
                <CustomTable
                  tableSources={winesData}
                  tableOptions={tableOptions.wines}
                  loading={dataLoading.loading}
                  filteredParams={queryParams}
                  setFilteredParams={setFilter}
                  getDataOnChange={getWinesLists}
                  toolbarView={toolbarView}
                  setSelectedRows={setSelectedRows}
                  selectedRows={selectedRows}
                  openPath={openPath}
                  withOpenAction={false}
                  withEditAction
                  withCloneAction
                  handleCloneAction={handleCloneAction}
                  disableCondition={row => !row.active}
                />
              </Box>
            </Grid>
          )}
        </Box>
      </Grid>
    </Box>
  );
});

Wines.propTypes = {
  path: PropTypes.string.isRequired,
};

export default Wines;
